import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ChartScope } from '../../../models/perivis-report-options.model';

export interface ChartConfigData {
  scope: ChartScope;
  parameter?: string;
  parameters?: string[];
  loadCaseNames?: string[];
}

export interface ChartConfigOptions {
  label: string;
  value: string;
}

@Component({
  selector: 'app-chart-config',
  template: `
    <div class="mb-4 p-3 border rounded">
      <h4 class="underline mb-2">{{ title }}</h4>
      <div class="space-y-3">
        <div>
          <label class="block text-sm mb-1">Scope</label>
          <div class="flex gap-4">
            <div class="flex items-center">
              <p-checkbox
                [binary]="true"
                [ngModel]="includeMultiple"
                (ngModelChange)="onScopeCheckboxChange('multiple', $event)"
                [inputId]="inputIdPrefix + '-multi'"
                pTooltip="One chart comparing selected parameters across all load cases"
                tooltipPosition="top"
              />
              <label [for]="inputIdPrefix + '-multi'" class="ml-2">Multiple Load Cases</label>
            </div>
            <div class="flex items-center">
              <p-checkbox
                [binary]="true"
                [ngModel]="includeSingle"
                (ngModelChange)="onScopeCheckboxChange('single', $event)"
                [inputId]="inputIdPrefix + '-single'"
                pTooltip="One chart per load case showing all parameters"
                tooltipPosition="top"
              />
              <label [for]="inputIdPrefix + '-single'" class="ml-2">Single Load Case</label>
            </div>
          </div>
        </div>
        @if (includeMultiple) {
          <div>
            <label class="block text-sm mb-1">Parameters</label>
            <p-multiSelect
              class="w-full"
              [(ngModel)]="config.parameters"
              [options]="parameterOptions"
              placeholder="Select parameters"
              display="chip"
              [maxSelectedLabels]="4"
              selectedItemsLabel="{0} items selected"
              (onChange)="onParametersChange()"
              appendTo="body"
            />
          </div>
        }
        @if (includeSingle && loadCaseOptions?.length > 0) {
          <div>
            <label class="block text-sm mb-1">Load Cases (optional)</label>
            <p-multiSelect
              class="w-full"
              [(ngModel)]="config.loadCaseNames"
              [options]="loadCaseOptions"
              placeholder="Leave empty for all load cases"
              display="chip"
              [maxSelectedLabels]="4"
              selectedItemsLabel="{0} selected"
              (onChange)="onLoadCaseNamesChange()"
              appendTo="body"
            />
          </div>
        }
      </div>
    </div>
  `,
  standalone: false
})
export class ChartConfigComponent {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) config!: ChartConfigData;
  @Input({ required: true }) parameterOptions!: ChartConfigOptions[];
  @Input({ required: true }) inputIdPrefix!: string;
  @Input() loadCaseOptions: ChartConfigOptions[] = [];

  @Output() scopeChange = new EventEmitter<ChartScope>();
  @Output() parametersChange = new EventEmitter<string[] | undefined>();
  @Output() loadCaseNamesChange = new EventEmitter<string[] | undefined>();

  get includeMultiple(): boolean {
    return this.config.scope === ChartScope.MultipleLoadCases || this.config.scope === ChartScope.Both;
  }

  get includeSingle(): boolean {
    return this.config.scope === ChartScope.SingleLoadCase || this.config.scope === ChartScope.Both;
  }

  onScopeCheckboxChange(which: 'multiple' | 'single', checked: boolean): void {
    const multi = which === 'multiple' ? checked : this.includeMultiple;
    const single = which === 'single' ? checked : this.includeSingle;

    if (multi && single) this.config.scope = ChartScope.Both;
    else if (multi) this.config.scope = ChartScope.MultipleLoadCases;
    else if (single) this.config.scope = ChartScope.SingleLoadCase;
    else this.config.scope = ChartScope.MultipleLoadCases; // at least one must be active

    this.scopeChange.emit(this.config.scope);
  }

  onParametersChange(): void {
    this.parametersChange.emit(this.config.parameters);
  }

  onLoadCaseNamesChange(): void {
    this.loadCaseNamesChange.emit(this.config.loadCaseNames);
  }
}
